import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { ROLES, License, LicenseStatus } from '../types';

export interface Role {
  id: string | number;
  name: string;
}

export interface UserState {
  userId: string | number;
  username?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  email?: string;
  isActive: boolean;
  roles?: Role[];
  firstTimeLogin?: boolean;
  companyName?: string;
  companyID?: string;
  licenseType?: string;
  licenseExpireDate?: string;
  licenseRemainingDays?: number;
  licenses?: License[];
}

const initialState: UserState = {
  userId: 0,
  isActive: true,
};

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    setAvatar: (state, action: PayloadAction<string>) => {
      state.avatar = action.payload;
    },
    setUser: (state, action: PayloadAction<UserState>) => {
      state = { ...action.payload };
      return state;
    },
    setLicenses: (state, action: PayloadAction<License[]>) => {
      state.licenses = action.payload;
    },
    clearUser: () => {
      return initialState;
    },
  },
});

export const { setUsername, setAvatar, setUser, setLicenses, clearUser } =
  profileSlice.actions;

export default profileSlice.reducer;

export const selectUsername = (state: RootState) =>
  String(state.profile.username || '');

export const selectEmail = (state: RootState) =>
  String(state.profile.email || '');

export const selectUserId = (state: RootState) => state.profile.userId;

export const selectUser = (state: RootState) => state.profile;

export const selectHasProfile = (state: RootState) =>
  !!state.profile.username && state.profile.userId !== 0;

export const selectFirstTimeLogin = (state: RootState) =>
  state.profile.firstTimeLogin;

export const selectRoles = (state: RootState) => state.profile.roles;

export const selectIsSuperAdmin = (state: RootState) =>
  !!state.profile.roles?.find(({ name }) => name === ROLES.ROLE_SUPERADMIN);

export const selectIsAdmin = (state: RootState) =>
  !!state.profile.roles?.find(({ name }) => name === ROLES.ROLE_ADMIN);

export const selectIsWorker = (state: RootState) =>
  !!state.profile.roles?.find(({ name }) => name === ROLES.ROLE_WORKER);

export const selectIsExpert = (state: RootState) =>
  !!state.profile.roles?.find(({ name }) => name === ROLES.ROLE_EXPERT);

export const selectLicenses = (state: RootState) =>
  state.profile.licenses || [];

export const selectActiveLicenses = (state: RootState) =>
  (state.profile.licenses || []).filter(
    (i) => i.status === LicenseStatus.ACTIVE
  );
